// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from 'react';

// react-router components
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from 'auth';
import { createProfileRequest } from 'profile';
import { fetchServiceByUuid } from 'service';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @mui core components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import TelegramIcon from '@mui/icons-material/Telegram';

// Soft UI Dashboard PRO React components
import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import { AdminLocaleSwitcher } from 'components/admin-locale-switcher';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';
import { stripLocalePrefix, withLocale } from 'lib/i18n/path';

// Soft UI Dashboard PRO React example components
import Breadcrumbs from 'examples/Breadcrumbs';
import { getSiteSectionTitle } from 'site';

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from 'examples/Navbars/DashboardNavbar/styles';

// Soft UI Dashboard PRO React context
import { useSoftUIController, setTransparentNavbar, setMiniSidenav } from 'context';

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const {
    account,
    accessToken,
    isAuthenticated,
    signOut,
    profiles: providers,
    activeProfileUuid,
    isProfilesLoading: isProvidersLoading,
    setActiveProfileUuid,
    registerProfile,
  } = useAuth();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [isCreateProfileDialogOpen, setIsCreateProfileDialogOpen] = useState(false);
  const [createProfileTitle, setCreateProfileTitle] = useState('');
  const [createProfileDescription, setCreateProfileDescription] = useState('');
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [createProfileErrorMessage, setCreateProfileErrorMessage] = useState('');
  const [activeServiceTitle, setActiveServiceTitle] = useState('');
  const location = useLocation();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const chrome = translations.adminChrome;
  const normalizedPathname = stripLocalePrefix(location.pathname).pathname;
  const route = normalizedPathname.split('/').filter(Boolean);
  const supportUrl = process.env.REACT_APP_SUPPORT_URL?.trim() ?? '';
  const circularNavbarButtonSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    minWidth: 34,
    p: 0,
    lineHeight: 0,
  };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType('sticky');
    } else {
      setNavbarType('static');
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener('scroll', handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleOpenProfileMenu = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenuAnchor(null);
  const handleSignOut = () => {
    handleCloseProfileMenu();
    signOut();
  };

  useEffect(() => {
    if (route[0] !== 'services' || !route[1]) {
      setActiveServiceTitle('');
      return;
    }

    let isCancelled = false;
    const serviceUuid = route[1];

    fetchServiceByUuid(serviceUuid, accessToken ?? undefined)
      .then((service) => {
        if (isCancelled) {
          return;
        }

        const normalizedServiceTitle = service?.title?.trim();
        setActiveServiceTitle(normalizedServiceTitle || chrome.serviceFallback);
      })
      .catch(() => {
        if (!isCancelled) {
          setActiveServiceTitle(chrome.serviceFallback);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, chrome.serviceFallback, route[0], route[1]]);

  const getProfileDisplayName = (provider) => {
    const normalizedTitle = provider?.title?.trim();
    if (!normalizedTitle) {
      return chrome.profileFallback;
    }

    const titleParts = normalizedTitle.split(/\s+/).filter(Boolean);
    if (titleParts.length < 2) {
      return normalizedTitle;
    }

    return `${titleParts[0]} ${titleParts[titleParts.length - 1]}`;
  };

  const getProfileBreadcrumbTitle = (provider) => {
    const normalizedTitle = provider?.title?.trim();
    if (normalizedTitle) {
      return normalizedTitle;
    }

    const normalizedFirstName = provider?.firstName?.trim();
    const normalizedLastName = provider?.lastName?.trim();
    const fullName = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ').trim();

    if (fullName) {
      return fullName;
    }

    return chrome.profileFallback;
  };

  const getAccountInitials = (value) => {
    const normalizedValue = value?.trim();
    if (!normalizedValue) {
      return '';
    }

    const lettersOnlyValue = normalizedValue.replace(/[^A-Za-z\s._@-]+/g, ' ');
    const valueParts = lettersOnlyValue.split(/[\s._@-]+/).filter(Boolean);
    if (valueParts.length === 0) {
      return '';
    }

    if (valueParts.length === 1) {
      return valueParts[0].slice(0, 2).toUpperCase();
    }

    return `${valueParts[0][0]}${valueParts[valueParts.length - 1][0]}`.toUpperCase();
  };

  const activeProvider =
    providers.find((provider) => provider.uuid === activeProfileUuid) ?? providers[0] ?? null;
  const { breadcrumbRoute, breadcrumbTitle } = useMemo(() => {
    if (route[0] === 'proposals') {
      return {
        breadcrumbRoute: ['proposals'],
        breadcrumbTitle: translations.proposalsPage.title,
      };
    }

    if (route[0] === 'galleries') {
      return {
        breadcrumbRoute: ['galleries'],
        breadcrumbTitle: translations.galleriesPage.title,
      };
    }

    if (route[0] === 'reviews') {
      return {
        breadcrumbRoute: ['reviews'],
        breadcrumbTitle: translations.reviewsPage.title,
      };
    }

    if (route[0] === 'site') {
      return {
        breadcrumbRoute: route.length > 1 ? ['site', route[1]] : ['site'],
        breadcrumbTitle:
          route.length > 1
            ? getSiteSectionTitle(route.slice(1), translations.sitePage)
            : translations.sitePage.title,
      };
    }

    if (route[0] === 'services' && route[1]) {
      const resolvedServiceTitle = activeServiceTitle || chrome.serviceFallback;

      return {
        breadcrumbRoute: ['services', resolvedServiceTitle],
        breadcrumbTitle: resolvedServiceTitle,
      };
    }

    if (route[0] !== 'profile' || !route[1]) {
      return {
        breadcrumbRoute: route,
        breadcrumbTitle: route[route.length - 1] || chrome.homeBreadcrumb,
      };
    }

    const routeProfileUuid = route[1];
    const routeProfile =
      providers.find((provider) => provider.uuid === routeProfileUuid) ??
      (activeProvider?.uuid === routeProfileUuid ? activeProvider : null);

    return {
      breadcrumbRoute: route,
      breadcrumbTitle: getProfileBreadcrumbTitle(routeProfile),
    };
  }, [
    activeProvider,
    activeServiceTitle,
    chrome.homeBreadcrumb,
    chrome.serviceFallback,
    providers,
    route,
    translations.proposalsPage.title,
    translations.reviewsPage.title,
    translations.sitePage,
  ]);
  const accountUsername = account?.username?.trim() || chrome.accountFallback;
  const accountInitials = getAccountInitials(account?.username);
  const activeProfileAvatarUrl =
    typeof activeProvider?.avatarUrl === 'string' && activeProvider.avatarUrl.trim()
      ? activeProvider.avatarUrl.trim()
      : undefined;
  const manageProfileRoute = activeProfileUuid
    ? withLocale(`/profile/${activeProfileUuid}`, locale)
    : withLocale('/pages/account/settings', locale);

  useEffect(() => {
    if (createProfileErrorMessage) {
      showSuiErrorSnackbar(createProfileErrorMessage);
    }
  }, [createProfileErrorMessage]);

  const handleProviderSelect = (providerUuid) => {
    setActiveProfileUuid(providerUuid);
    handleCloseProfileMenu();
  };

  const handleOpenAddProfileDialog = () => {
    handleCloseProfileMenu();
    setCreateProfileErrorMessage('');
    setIsCreateProfileDialogOpen(true);
  };

  const handleCloseAddProfileDialog = () => {
    if (isCreatingProfile) {
      return;
    }

    setIsCreateProfileDialogOpen(false);
    setCreateProfileTitle('');
    setCreateProfileDescription('');
    setCreateProfileErrorMessage('');
  };

  const handleCreateProfile = async (event) => {
    event.preventDefault();

    const normalizedTitle = createProfileTitle.trim();
    const normalizedDescription = createProfileDescription.trim();

    if (normalizedTitle.length < 2) {
      setCreateProfileErrorMessage(chrome.createProfileValidation);
      return;
    }

    if (!account?.uuid) {
      setCreateProfileErrorMessage(chrome.createProfileAccountMissing);
      return;
    }

    setIsCreatingProfile(true);
    setCreateProfileErrorMessage('');

    try {
      const createdProfile = await createProfileRequest(
        {
          accountUuid: account.uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
        },
        accessToken ?? undefined
      );

      registerProfile(createdProfile);
      handleCloseAddProfileDialog();
    } catch (error) {
      setCreateProfileErrorMessage(
        error instanceof Error ? error.message : translations.createProfilePage.createFailed
      );
    } finally {
      setIsCreatingProfile(false);
    }
  };

  const renderProfileMenu = () => (
    <Menu
      id="profile-switcher-menu"
      anchorEl={profileMenuAnchor}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(profileMenuAnchor)}
      onClose={handleCloseProfileMenu}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 300,
        },
      }}
    >
      <SoftBox px={2} pt={2} pb={1} textAlign="center">
        <SoftTypography variant="button" fontWeight="bold">
          {accountUsername}
        </SoftTypography>
      </SoftBox>
      <SoftBox px={2} pb={2} display="flex" justifyContent="center">
        <Avatar
          src={activeProfileAvatarUrl}
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'info.main',
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          {accountInitials || <Icon>person</Icon>}
        </Avatar>
      </SoftBox>
      <Divider />

      <MenuItem component={Link} to={manageProfileRoute} onClick={handleCloseProfileMenu}>
        <Icon fontSize="small" sx={{ mr: 1 }}>
          manage_accounts
        </Icon>
        <SoftTypography variant="button" fontWeight="regular">
          {chrome.manageProfile}
        </SoftTypography>
      </MenuItem>
      <Divider />

      <SoftBox px={2} pt={1} pb={0.5}>
        <SoftTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
        >
          {chrome.profilesLabel}
        </SoftTypography>
      </SoftBox>

      {isProvidersLoading ? (
        <MenuItem disabled>
          <SoftTypography variant="button" fontWeight="regular">
            {chrome.profilesLoading}
          </SoftTypography>
        </MenuItem>
      ) : providers.length > 0 ? (
        providers.map((provider) => {
          const isActive = provider.uuid === activeProvider?.uuid;

          return (
            <MenuItem
              key={provider.uuid}
              selected={isActive}
              onClick={() => handleProviderSelect(provider.uuid)}
              sx={{ minWidth: 240, gap: 1 }}
            >
              <SoftBox flexGrow={1} minWidth={0}>
                <SoftTypography variant="button" fontWeight={isActive ? 'bold' : 'regular'}>
                  {getProfileDisplayName(provider)}
                </SoftTypography>
                {provider.title !== getProfileDisplayName(provider) ? (
                  <SoftTypography variant="caption" color="text">
                    {provider.title}
                  </SoftTypography>
                ) : null}
              </SoftBox>
              {isActive ? (
                <Icon fontSize="small" color="info">
                  check
                </Icon>
              ) : null}
            </MenuItem>
          );
        })
      ) : (
        <MenuItem disabled>
          <SoftTypography variant="button" fontWeight="regular">
            {chrome.profilesEmpty}
          </SoftTypography>
        </MenuItem>
      )}

      <MenuItem onClick={handleOpenAddProfileDialog}>
        <Icon fontSize="small" sx={{ mr: 1 }}>
          person_add
        </Icon>
        <SoftTypography variant="button" fontWeight="regular">
          {chrome.addProfile}
        </SoftTypography>
      </MenuItem>

      <Divider />

      <MenuItem onClick={handleSignOut}>
        <Icon fontSize="small" sx={{ mr: 1 }}>
          logout
        </Icon>
        <SoftTypography variant="button" fontWeight="regular">
          {translations.auth.signOut}
        </SoftTypography>
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? 'absolute' : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={breadcrumbTitle} route={breadcrumbRoute} light={light} />
          <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
            {miniSidenav ? 'menu_open' : 'menu'}
          </Icon>
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox
              color={light ? 'white' : 'inherit'}
              display="flex"
              alignItems="center"
              gap={0.75}
            >
              <AdminLocaleSwitcher light={light} />
              {supportUrl ? (
                <Tooltip title={translations.auth.supportTooltip}>
                  <IconButton
                    component="a"
                    href={supportUrl}
                    target="_blank"
                    rel="noreferrer"
                    sx={(theme) => ({
                      ...circularNavbarButtonSx,
                      backgroundImage: theme.functions.linearGradient(
                        theme.palette.gradients.primary.main,
                        theme.palette.gradients.primary.state
                      ),
                      boxShadow: 'none',
                      color: theme.palette.white.main,
                      transition: theme.transitions.create(['transform', 'filter'], {
                        easing: theme.transitions.easing.easeInOut,
                        duration: theme.transitions.duration.shorter,
                      }),
                      '& .MuiSvgIcon-root': {
                        display: 'block',
                        color: theme.palette.white.main,
                      },
                    })}
                    aria-label={translations.auth.openSupport}
                  >
                    <TelegramIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {isAuthenticated ? (
                <IconButton
                  sx={circularNavbarButtonSx}
                  size="small"
                  onClick={handleOpenProfileMenu}
                  aria-controls="profile-switcher-menu"
                  aria-haspopup="true"
                  aria-expanded={Boolean(profileMenuAnchor) ? 'true' : undefined}
                  aria-label={translations.auth.openAccountMenu}
                >
                  <Avatar
                    src={activeProfileAvatarUrl}
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: 'info.main',
                      fontWeight: 'bold',
                      fontSize: '0.78rem',
                    }}
                  >
                    {accountInitials || <Icon>person</Icon>}
                  </Avatar>
                </IconButton>
              ) : (
                <Link to={withLocale('/sign-in', locale)}>
                  <IconButton sx={navbarIconButton} size="small">
                    <Icon
                      sx={({ palette: { dark, white } }) => ({
                        color: light ? white.main : dark.main,
                      })}
                    >
                      account_circle
                    </Icon>
                    <SoftTypography
                      variant="button"
                      fontWeight="medium"
                      color={light ? 'white' : 'dark'}
                    >
                      {translations.auth.signIn}
                    </SoftTypography>
                  </IconButton>
                </Link>
              )}
              <IconButton
                size="small"
                color="inherit"
                sx={(theme) => ({
                  ...circularNavbarButtonSx,
                  ...navbarMobileMenu(theme),
                })}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? 'text-white' : 'text-dark'}>
                  {miniSidenav ? 'menu_open' : 'menu'}
                </Icon>
              </IconButton>
              {renderProfileMenu()}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
      <Dialog
        open={isCreateProfileDialogOpen}
        onClose={handleCloseAddProfileDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{chrome.createProfileDialogTitle}</DialogTitle>
        <SoftBox component="form" onSubmit={handleCreateProfile}>
          <DialogContent>
            <Stack spacing={2} mt={0.5}>
              <SoftInput
                autoFocus
                placeholder={chrome.createProfileTitlePlaceholder}
                value={createProfileTitle}
                onChange={(event) => setCreateProfileTitle(event.target.value)}
              />
              <SoftInput
                placeholder={chrome.createProfileDescriptionPlaceholder}
                value={createProfileDescription}
                onChange={(event) => setCreateProfileDescription(event.target.value)}
                multiline
                rows={3}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton
              variant="outlined"
              color="secondary"
              onClick={handleCloseAddProfileDialog}
              disabled={isCreatingProfile}
            >
              {chrome.cancel}
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={isCreatingProfile || createProfileTitle.trim().length < 2}
            >
              {isCreatingProfile ? chrome.createProfileSubmitting : chrome.createProfileSubmit}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
